import { parseCard, RANK_LABEL, type CardId } from '@mendikot/engine';
import type { CardBackStyle } from '@mendikot/design-tokens';
import clsx from 'clsx';
import { memo, useId, type CSSProperties } from 'react';
import { useSettings } from '../../app/context';
import { useTheme } from '../../app/theme';
import { EmblemShape } from './Emblem';
import { SuitShape, suitColor } from './SuitIcon';

const RADIUS = '9% / 6.4%';

/** Vector card face in a 100×140 box; scales crisply from tiny trick cards to large hand cards. */
export const CardFace = memo(function CardFace({ cardId }: { cardId: CardId }) {
  const fourColor = useSettings((s) => s.fourColor);
  const { suit, rank } = parseCard(cardId);
  const color = suitColor(suit, fourColor);
  const label = RANK_LABEL[rank];
  const isCourt = rank >= 11 && rank <= 13;
  const corner = (
    <g>
      <text
        x={label.length > 1 ? 5 : 8}
        y={27}
        fontSize={label.length > 1 ? 23 : 27}
        fontWeight={800}
        fill={color}
        fontFamily="Manrope, system-ui, sans-serif"
        letterSpacing={label.length > 1 ? -2 : 0}
      >
        {label}
      </text>
      <svg x={8} y={32} width={17} height={17} viewBox="0 0 100 100" fill={color}>
        <SuitShape suit={suit} />
      </svg>
    </g>
  );
  return (
    <svg viewBox="0 0 100 140" className="block h-full w-full" aria-hidden="true">
      <rect
        x="1"
        y="1"
        width="98"
        height="138"
        rx="9"
        fill="var(--c-card-face)"
        stroke="var(--c-card-edge)"
        strokeWidth="1.5"
      />
      {corner}
      <g transform="rotate(180 50 70)">{corner}</g>
      {isCourt ? (
        <g>
          <rect
            x="24"
            y="30"
            width="52"
            height="80"
            rx="7"
            fill={color}
            fillOpacity="0.07"
            stroke={color}
            strokeOpacity="0.45"
            strokeWidth="1.5"
          />
          <text
            x="50"
            y="84"
            textAnchor="middle"
            fontSize="44"
            fill={color}
            fontFamily="'Yatra One', Georgia, serif"
          >
            {label}
          </text>
          <svg x={42} y={36} width={16} height={16} viewBox="0 0 100 100" fill={color}>
            <SuitShape suit={suit} />
          </svg>
          <svg x={42} y={90} width={16} height={16} viewBox="0 0 100 100" fill={color}>
            <SuitShape suit={suit} />
          </svg>
        </g>
      ) : (
        <svg
          x={rank === 14 ? 20 : 28}
          y={rank === 14 ? 40 : 48}
          width={rank === 14 ? 60 : 44}
          height={rank === 14 ? 60 : 44}
          viewBox="0 0 100 100"
          fill={color}
        >
          <SuitShape suit={suit} />
        </svg>
      )}
      {rank === 10 && (
        // Tens decide the hand — a brass medallion makes them easy to spot.
        <g>
          <circle
            cx="84"
            cy="16"
            r="8"
            fill="var(--c-accent)"
            stroke="var(--c-card-face)"
            strokeWidth="2"
          />
          <path
            d="M84 10.5l1.6 3.4 3.7.4-2.8 2.5.8 3.6-3.3-1.9-3.3 1.9.8-3.6-2.8-2.5 3.7-.4z"
            fill="var(--c-accent-text)"
          />
        </g>
      )}
    </svg>
  );
});

function BackPattern({ style, id }: { style: CardBackStyle; id: string }) {
  const stroke = 'var(--c-card-back-pattern)';
  switch (style) {
    case 'tricolor':
      return (
        <g>
          <rect x="8" y="8" width="84" height="41" fill="#ff9933" />
          <rect x="8" y="49" width="84" height="42" fill="#fdf9f0" />
          <rect x="8" y="91" width="84" height="41" fill="#138808" />
        </g>
      );
    case 'diya':
    case 'marigold':
    case 'pookalam':
    case 'rangoli':
    case 'alpana':
    case 'bandhani':
      return (
        <>
          <defs>
            <pattern id={id} width="12" height="12" patternUnits="userSpaceOnUse">
              <circle
                cx="6"
                cy="6"
                r={style === 'bandhani' ? 1.6 : 2.2}
                fill={stroke}
                opacity="0.55"
              />
              <circle cx="0" cy="0" r="1" fill={stroke} opacity="0.35" />
              <circle cx="12" cy="12" r="1" fill={stroke} opacity="0.35" />
            </pattern>
          </defs>
          <rect x="8" y="8" width="84" height="124" fill={`url(#${id})`} />
        </>
      );
    case 'gulal':
      return (
        <g opacity="0.75">
          <circle cx="26" cy="30" r="18" fill="#ffd23f" />
          <circle cx="74" cy="44" r="20" fill="#3ec1d3" />
          <circle cx="34" cy="96" r="22" fill="#ff5fa2" />
          <circle cx="76" cy="112" r="16" fill="#7bd389" />
        </g>
      );
    case 'crescent':
    case 'snowflake':
    case 'rakhi':
      return (
        <>
          <defs>
            <pattern id={id} width="16" height="16" patternUnits="userSpaceOnUse">
              <path
                d="M8 3l1.2 3.3L12.5 8 9.2 9.2 8 12.5 6.8 9.2 3.5 8l3.3-1.7z"
                fill={stroke}
                opacity="0.5"
              />
            </pattern>
          </defs>
          <rect x="8" y="8" width="84" height="124" fill={`url(#${id})`} />
        </>
      );
    default:
      return (
        <>
          <defs>
            <pattern
              id={id}
              width="14"
              height="14"
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <path d="M0 7h14M7 0v14" stroke={stroke} strokeWidth="0.9" opacity="0.5" />
            </pattern>
          </defs>
          <rect x="8" y="8" width="84" height="124" fill={`url(#${id})`} />
        </>
      );
  }
}

export const CardBack = memo(function CardBack() {
  const theme = useTheme();
  const id = useId().replace(/:/g, '');
  return (
    <svg viewBox="0 0 100 140" className="block h-full w-full" aria-hidden="true">
      <rect
        x="1"
        y="1"
        width="98"
        height="138"
        rx="9"
        fill="var(--c-card-back)"
        stroke="rgba(0,0,0,0.25)"
        strokeWidth="1.5"
      />
      <BackPattern style={theme.cardBack} id={`p${id}`} />
      <rect
        x="8"
        y="8"
        width="84"
        height="124"
        rx="5"
        fill="none"
        stroke="var(--c-card-back-pattern)"
        strokeWidth="1.6"
        opacity="0.9"
      />
      <g
        transform="translate(30 50) scale(0.4)"
        color="var(--c-card-back-pattern)"
        style={{ color: 'var(--c-card-back-pattern)' }}
      >
        <circle cx="50" cy="50" r="46" fill="var(--c-card-back)" />
        <EmblemShape emblem={theme.emblem} />
      </g>
    </svg>
  );
});

export interface PlayingCardProps {
  cardId: CardId | null;
  /** Show the back. With `flippable`, both faces are rendered for 3D flips. */
  faceDown?: boolean;
  flippable?: boolean;
  className?: string;
  style?: CSSProperties;
}

/** A card sized by its container width (5:7). Shadows live on the wrapper so SVGs stay cheap. */
export function PlayingCard({ cardId, faceDown, flippable, className, style }: PlayingCardProps) {
  const showBack = faceDown || cardId === null;
  return (
    <div
      className={clsx(
        'relative aspect-[5/7] shadow-[0_6px_14px_-6px_rgba(0,0,0,0.7)]',
        flippable && 'card-3d',
        className,
      )}
      style={{ borderRadius: RADIUS, ...style }}
    >
      {flippable ? (
        <>
          <div className="card-face-side absolute inset-0">
            {cardId ? <CardFace cardId={cardId} /> : <CardBack />}
          </div>
          <div className="card-face-side absolute inset-0" style={{ transform: 'rotateY(180deg)' }}>
            <CardBack />
          </div>
        </>
      ) : showBack ? (
        <CardBack />
      ) : (
        <CardFace cardId={cardId!} />
      )}
    </div>
  );
}

export const CARD_RADIUS = RADIUS;
